/**
 * Recover from a stale build after a deploy.
 *
 * A tab opened before a deploy still runs the old main.*.js, which asks for
 * lazy chunks the new build no longer has. The server answers with index.html,
 * so the chunk fails to load (ChunkLoadError / "Unexpected token '<'") and the
 * page goes blank. Reloading once fetches the new index.html and its chunks.
 *
 * Reloads at most once per minute per tab: if the fresh page fails the same way
 * (a genuinely broken deploy), it stops instead of looping.
 */
const KEY = 'rlx-chunk-reload-at';
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

export function isChunkLoadError(error) {
  if (!error) return false;
  const message = String(error?.message ?? error);
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

/** @returns {boolean} true when a reload was started */
export function reloadOnceForChunkError(error, {
  storage = typeof window !== 'undefined' ? window.sessionStorage : null,
  now = Date.now(),
  reload = () => window.location.reload(),
} = {}) {
  if (!isChunkLoadError(error) || !storage) return false;
  let last = null;
  try { last = storage.getItem(KEY); } catch { return false; }
  if (last !== null && now - Number(last) < CHUNK_RELOAD_WINDOW_MS) return false;
  // If the attempt cannot be recorded, a reload could loop; leave the page as is.
  try { storage.setItem(KEY, String(now)); } catch { return false; }
  reload();
  return true;
}

export function installChunkReloadGuard() {
  window.addEventListener('error', (event) => { reloadOnceForChunkError(event.error ?? event.message); });
  window.addEventListener('unhandledrejection', (event) => { reloadOnceForChunkError(event.reason); });
}
